"use client";

import React, { type JSX, useId } from "react";
import styled from "styled-components";
import { FileJsonIcon } from "lucide-react";
import {
  ClipboardIcon,
  EyeIcon,
  EyeSlashIcon,
  TextAaIcon,
} from "@phosphor-icons/react";
import { Tooltip } from "react-tooltip";

import {
  useTextInputField,
  type PasswordRequirement,
  type ImportProp,
} from "./useTextInputField";

// --- Styled Components ---

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LabelRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: inherit;
  gap: 5px;
  align-items: flex-start;
  justify-content: flex-start;
  p {
    margin: 0;
  }
`;

const RequiredAsterisk = styled.p`
  font-size: ${({ theme }) => theme.typography.sizes.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.error};
  text-align: left;
  user-select: none;
`;

const Label = styled.label<{ $ishelper: boolean }>`
  display: block;
  font-size: ${({ theme }) => theme.typography.sizes.label};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  @media (max-width: 768px) {
    font-size: ${({ $ishelper }) => ($ishelper ? "16px" : "18px")};
  }
`;

const InputField = styled.input<{ $haserror: boolean; $isPassword?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: ${({ $isPassword }) => ($isPassword ? "3rem" : "1rem")};
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: 8px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.primaryBackground};
  transition: all 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(234, 172, 102, 0.1);
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.6;
  }
`;

const HelperText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.sizes.helper};
  margin-top: ${({ theme }) => theme.spacing.small};
  text-align: right;
  align-self: flex-end;
  user-select: none;
  width: 100%;
`;

const ToggleIcon = styled.button<{ disabled: boolean }>`
  width: 50px;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding-right: 15px;
`;

const CharacterCount = styled.div<{ $isexceeded: boolean }>`
  align-self: flex-end;
  font-size: ${({ theme }) => theme.typography.sizes.helper};
  color: ${({ theme, $isexceeded }) =>
    $isexceeded ? theme.colors.error : theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.small};
`;

const ImportButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0;
  margin-left: 5px;
  transition: all 0.3s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const HintText = styled.span`
  font-size: ${({ theme }) => theme.typography.sizes.body};
  text-align: right;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.textSecondary};
  user-select: none;
  margin-top: ${({ theme }) => theme.spacing.medium};
`;

// --- Component Props ---

export interface CustomInputFieldProps {
  label: string;
  required?: boolean;
  isLabelHint?: boolean;
  type?: "email" | "password" | "url" | null | undefined;
  passwordType?: PasswordRequirement;
  overwidth?: boolean;
  currentValue?: string;
  regex?: "alphanumeric" | "alphanumeric-code" | "numbers" | "letters" | "all";
  onChange?: (value: string) => void;
  onBlur?: () => void;
  maxChar?: number;
  minChar?: number;
  allcaps?: boolean;
  onValidated?: (valid: boolean) => void;
  className?: string;
  disabled?: boolean;
  capitalize?: "word" | "character" | "sentence" | "first" | null;
  whitelist?: string[];
  importProp?: ImportProp;
  helper?: string;
  autofocus?: boolean;
  sensitive?: boolean;
  placeholder?: string;
}

// --- Component ---

const CustomInputField: React.FC<CustomInputFieldProps> = (props) => {
  const {
    label,
    required = false,
    isLabelHint = false,
    disabled = false,
    className,
    helper,
    autofocus = false,
    sensitive = false,
    placeholder,
    importProp,
    maxChar = 0,
  } = props;

  const tooltipId = useId();

  const {
    inputValue,
    hasError,
    errorMessage,
    charCount,
    showPassword,
    isPasswordType,
    isExceeded,
    handleChange,
    handleBlurSafe,
    handleKeyDown,
    togglePasswordVisibility,
    handleJsonImport,
    handleTextImport,
    handleClipboardImport,
  } = useTextInputField(props);

  const renderImportIcons = (): JSX.Element | null => {
    if (!importProp) return null;
    const icons: JSX.Element[] = [];
    const pushIcon = (
      tooltip: string,
      icon: JSX.Element,
      onClick: () => void,
    ): number =>
      icons.push(
        <ImportButton
          key={tooltip}
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={tooltip}
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltip}
        >
          {icon}
        </ImportButton>,
      );

    switch (importProp.type) {
      case "json":
        pushIcon(
          "Import a JSON file",
          <FileJsonIcon width={16} />,
          handleJsonImport,
        );
        break;
      case "txt":
        pushIcon(
          "Import a text file",
          <TextAaIcon width={16} />,
          handleTextImport,
        );
        break;
      case "clipboard":
        pushIcon(
          "Paste text from clipboard",
          <ClipboardIcon width={16} />,
          handleClipboardImport,
        );
        break;
      case "all":
        pushIcon(
          "Import a JSON file",
          <FileJsonIcon width={16} />,
          handleJsonImport,
        );
        pushIcon(
          "Import a text file",
          <TextAaIcon width={16} />,
          handleTextImport,
        );
        pushIcon(
          "Paste text from clipboard",
          <ClipboardIcon width={16} />,
          handleClipboardImport,
        );
        break;
    }

    return (
      <div style={{ display: "flex", gap: "6px", marginLeft: "5px" }}>
        {icons}
        <Tooltip
          id={tooltipId}
          place="top"
          delayShow={300}
          style={{ fontSize: "11px", borderRadius: "6px", padding: "6px 8px" }}
        />
      </div>
    );
  };

  return (
    <InputWrapper>
      {!isLabelHint && (
        <LabelRowContainer>
          <Label htmlFor="custom-input" $ishelper={isLabelHint}>
            {label}
          </Label>
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
          {renderImportIcons()}
        </LabelRowContainer>
      )}
      <div style={{ position: "relative" }}>
        <InputField
          id="custom-input"
          type={isPasswordType && !showPassword ? "password" : "text"}
          $isPassword={isPasswordType}
          $haserror={hasError}
          value={inputValue}
          onChange={handleChange}
          placeholder={
            placeholder?.trim() ? placeholder : isLabelHint ? label : ""
          }
          disabled={disabled}
          onBlur={handleBlurSafe}
          onKeyDown={handleKeyDown}
          autoFocus={autofocus}
          data-private={sensitive ? true : undefined}
          className={className}
        />
        {isPasswordType && (
          <ToggleIcon onClick={togglePasswordVisibility} disabled={disabled}>
            {!showPassword ? (
              <EyeSlashIcon width={20} />
            ) : (
              <EyeIcon width={20} />
            )}
          </ToggleIcon>
        )}
      </div>
      {maxChar !== 0 && (
        <CharacterCount $isexceeded={isExceeded}>
          {`${charCount}/${maxChar}`}
        </CharacterCount>
      )}
      {helper && <HintText>{helper}</HintText>}
      {hasError && <HelperText>{errorMessage}</HelperText>}
    </InputWrapper>
  );
};

export default CustomInputField;
